import { useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ForgotPasswordFlow } from "@/components/ForgotPasswordFlow";
import { Eye, EyeSlash, EnvelopeSimple, DeviceMobile, ArrowLeft } from "@phosphor-icons/react";
import logo from "@/assets/logo.png";

const tabCls = (active) =>
  `press flex-1 h-10 rounded-full text-sm font-bold border-2 border-black flex items-center justify-center gap-2 ${active ? "bg-primary text-white" : "bg-white text-black"}`;
const fieldCls = "mt-1 w-full h-12 px-4 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary";
const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-black/60";

export default function Login() {
  const { login, loginWithSession } = useAuth();
  const [mode, setMode] = useState("email"); // "email" | "phone"

  // Email login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Phone login state
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [otpStep, setOtpStep] = useState(1); // 1 = enter phone, 2 = enter code
  const [demoCode, setDemoCode] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    // Read straight from the inputs: on iOS/Safari a Face ID / autofilled value can land in the
    // DOM before React's onChange commits it to state, so the first Enter would otherwise submit
    // an empty value ("nothing happens, works on the 2nd try"). Refs use the real current value.
    const em = (emailRef.current?.value ?? email);
    const pw = (passwordRef.current?.value ?? password);
    setEmail(em); setPassword(pw);
    setError("");
    setBusy(true);
    try {
      await login(em, pw);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };


  const requestOtp = async (e) => {
    e?.preventDefault();
    setPhoneErr("");
    setDemoCode("");
    setPhoneBusy(true);
    try {
      const { data } = await api.post("/auth/phone/request-otp", { phone });
      if (data.demo_mode && data.demo_code) setDemoCode(data.demo_code);
      setOtpStep(2);
    } catch (err) {
      setPhoneErr(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPhoneBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setPhoneErr("");
    setPhoneBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify-otp", { phone, code });
      await loginWithSession(data.token, data.user);
    } catch (err) {
      setPhoneErr(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setPhoneBusy(false);
    }
  };

  const resetPhone = () => { setOtpStep(1); setCode(""); setDemoCode(""); setPhoneErr(""); };

  return (
    <div className="min-h-screen flex items-center justify-center p-5 bg-background">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={logo} alt="Short Stack Hawai'i" className="w-56 h-auto mb-2" data-testid="login-logo" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-black/50">Crew Operations</p>
        </div>

        <div className="bg-white border-2 border-black rounded-xl hard-shadow p-6">
          <h2 className="font-display text-2xl font-bold mb-1">Welcome back</h2>
          <p className="text-sm text-black/60 mb-5">Sign in to manage your shop's day.</p>

          {/* Method tabs */}
          <div className="flex gap-2 mb-5">
            <button data-testid="login-tab-email" onClick={() => { setMode("email"); setError(""); }} className={tabCls(mode === "email")}>
              <EnvelopeSimple size={16} weight="bold" /> Email
            </button>
            <button data-testid="login-tab-phone" onClick={() => { setMode("phone"); resetPhone(); }} className={tabCls(mode === "phone")}>
              <DeviceMobile size={16} weight="bold" /> Phone
            </button>
          </div>

          {mode === "email" && (
          <>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className={labelCls}>Email or username</label>
              <input
                data-testid="login-email-input" ref={emailRef}
                type="text" name="username" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className={fieldCls}
                placeholder="you@shop.com or username"
              />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <div className="relative mt-1">
                <input
                  data-testid="login-password-input" ref={passwordRef}
                  type={show ? "text" : "password"} name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full h-12 px-4 pr-12 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
                <button type="button" data-testid="toggle-password-btn" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/50">
                  {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && <p data-testid="login-error" className="text-sm font-bold text-primary">{error}</p>}

            <button
              data-testid="login-submit-btn" type="submit" disabled={busy}
              className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <button
            type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)}
            className="press mt-3 w-full text-center text-sm font-bold text-primary underline underline-offset-2"
          >
            Forgot your password?
          </button>
          </>
          )}

          {mode === "phone" && (
          <>
          {otpStep === 1 ? (
            <form onSubmit={requestOtp} className="space-y-4" data-testid="phone-request-form">
              <div>
                <label className={labelCls}>Mobile number</label>
                <input
                  data-testid="login-phone-input"
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required
                  className={fieldCls}
                  placeholder="+1 555 100 0001"
                />
                <p className="text-xs text-black/45 mt-1.5">We'll text you a 6-digit code. Use international format (e.g. +15551000001).</p>
              </div>
              {phoneErr && <p data-testid="phone-error" className="text-sm font-bold text-primary">{phoneErr}</p>}
              <button data-testid="send-code-btn" type="submit" disabled={phoneBusy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60">
                {phoneBusy ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4" data-testid="phone-verify-form">
              <button type="button" data-testid="phone-back-btn" onClick={resetPhone} className="press inline-flex items-center gap-1 text-xs font-bold text-black/60">
                <ArrowLeft size={14} weight="bold" /> Change number
              </button>
              <div>
                <label className={labelCls}>Enter the code sent to {phone}</label>
                <input
                  data-testid="login-code-input"
                  inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required
                  className={`${fieldCls} tracking-[0.5em] text-center font-bold text-lg`}
                  placeholder="••••••"
                />
              </div>
              {demoCode && (
                <p data-testid="demo-code-hint" className="text-xs font-bold text-black/70 bg-[#FEF9C3] border-2 border-black rounded-lg p-2.5">
                  Demo mode (no SMS provider configured): your code is <span className="text-primary">{demoCode}</span>.
                </p>
              )}
              {phoneErr && <p data-testid="phone-error" className="text-sm font-bold text-primary">{phoneErr}</p>}
              <button data-testid="verify-code-btn" type="submit" disabled={phoneBusy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60">
                {phoneBusy ? "Verifying…" : "Verify & sign in"}
              </button>
              <button type="button" data-testid="resend-code-btn" onClick={requestOtp} disabled={phoneBusy} className="press w-full h-10 rounded-full bg-white font-bold text-sm border-2 border-black disabled:opacity-60">
                Resend code
              </button>
            </form>
          )}
          </>
          )}
        </div>
        <p className="text-center text-xs text-black/50 mt-6" data-testid="login-legal-links">
          By continuing you agree to our{" "}
          <a href="/terms.html" className="underline font-bold">Terms &amp; Conditions</a>{" "}and{" "}
          <a href="/privacy.html" className="underline font-bold">Privacy Policy</a>.
        </p>
      </div>
      <ForgotPasswordFlow open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
